import api, { apiFormData } from "../../../utils/api";
import type { FirestoreTimestamp } from "./leadsService";

// ============================================================================
// CRM Outbound Campaigns — frontend service (issue #220 B).
// "Cargar Excel → lanzar campaña de llamadas salientes": upload a contacts file
// and enqueue throttled AI calls with a chosen agent. The backend creates the
// leads AND enqueues the calls through the call queue (concurrency, idempotency
// and do-not-call enforced server-side).
//   • launch (authed): POST /leads/bulk-campaign   (multipart)
//   • list   (authed): GET  /leads/campaigns
// ============================================================================

export interface CampaignTotals {
  rows: number;
  valid?: number;
  created: number;
  duplicates: number;
  invalid: number;
  queued: number;
  skipped: number;
}

export interface Campaign {
  id: string;
  name: string;
  crmAgentId: string | null;
  source: string;
  status: "launching" | "launched" | string;
  maxCalls: number | null;
  createdBy: string | null;
  totals: CampaignTotals;
  createdAt?: FirestoreTimestamp | string | null;
  updatedAt?: FirestoreTimestamp | string | null;
}

export interface LaunchCampaignInput {
  file: File;
  crmAgentId: string;
  campaignName: string;
  /** Cap how many imported leads are enqueued for calls (optional). */
  maxCalls?: number | null;
}

export interface LaunchCampaignResult {
  campaignId: string;
  name: string;
  crmAgentId: string | null;
  maxCalls: number | null;
  rows: number;
  created: number;
  duplicates: number;
  invalid: number;
  queued: number;
  skipped: number;
  errors: { row: number | null; reason: string }[];
}

/** Upload an Excel/CSV file and launch an outbound call campaign. */
export async function launchOutboundCampaign(
  input: LaunchCampaignInput,
): Promise<LaunchCampaignResult> {
  const form = new FormData();
  form.append("file", input.file);
  form.append("crmAgentId", input.crmAgentId);
  form.append("campaignName", input.campaignName);
  if (input.maxCalls != null) form.append("maxCalls", String(input.maxCalls));
  const res = await apiFormData.post("/leads/bulk-campaign", form);
  return (
    res.data?.data ?? {
      campaignId: "",
      name: input.campaignName,
      crmAgentId: input.crmAgentId,
      maxCalls: input.maxCalls ?? null,
      rows: 0,
      created: 0,
      duplicates: 0,
      invalid: 0,
      queued: 0,
      skipped: 0,
      errors: [],
    }
  );
}

/** List outbound campaigns (newest first). */
export async function fetchCampaigns(): Promise<Campaign[]> {
  const res = await api.get("/leads/campaigns");
  const arr = res.data?.data;
  return Array.isArray(arr) ? arr : [];
}
